import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { parseBML } from "../lib/bml-parser";
import { renderBML } from "../lib/bml-renderer";
import { applyUtilitiesToTargetRoot } from "../lib/runtimeCss";
import { encodeSeed } from "../lib/seedToken";
import { registerFontFacesFromCss } from "../lib/useFontFace";

interface BioData {
  id: string;
  userId?: string; // owner user id for canonical linking
  username: string;
  bio: string;
  bioMarkup: string;
  bioFormat?: "bml" | "html";
  bioHtml?: string;
  bioCss?: string;
  createdAt: string;
  updatedAt: string;
  viewsCount?: number;
  isOwner?: boolean; // server-provided ownership flag
  bioConfig?: {
    color?: string;
    favicon?: string;
    allowEdit?: boolean;
    title?: string;
    description?: string;
    image?: string;
    pageTitle?: string;
  };
}

import { BACKEND_URL } from "../config/env";
import { Flag, Loader2 } from "lucide-react";
import { ReportDialog } from "@/components/ui/report-dialog";
import { useSession } from "../lib/auth-client";
import {
  generateProfilePageSchema,
  StructuredData,
} from "@/lib/structured-data";

const BASE = BACKEND_URL || "";

export const Route = createFileRoute("/$username")({
  loader: async ({ params }: { params: { username?: string } }) => {
    const username = params.username;

    if (!username) {
      throw new Error("failed to load bio");
    }

    const response = await fetch(
      `${BASE}/api/${encodeURIComponent(username)}`,
      {
        headers: {
          Accept: "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("bio not found");
      }
      throw new Error("failed to load bio");
    }

    const bioData: BioData = await response.json();
    return bioData;
  },
  component: UserBioPage,
  errorComponent: ({ error }: { error: unknown }) => {
    const message =
      error instanceof Error ? error.message : "failed to load bio";
    const isNotFound = message === "bio not found";
    return (
      <div className="flex items-center justify-center min-h-svh bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-accent mb-2">
            {isNotFound ? "bio not found" : "something went wrong"}
          </h1>
          <p className="text-muted-foreground mb-2">
            {isNotFound
              ? "we couldn’t find a published bio for that username."
              : "please try again in a moment."}
          </p>
          <Link to="/" className="text-accent hover:underline">
            {"← back to home"}
          </Link>
        </div>
      </div>
    );
  },
});

function UserBioPage() {
  const bioData = Route.useLoaderData() as BioData | undefined;
  const hostRef = useRef<HTMLDivElement | null>(null);
  // page root ref so we can change background utilities dynamically
  const pageRootRef = useRef<HTMLDivElement | null>(null);
  // ref to container that holds rendered bml content for scanning
  const bmlContainerRef = useRef<HTMLDivElement | null>(null);
  // canonical owner username resolved from user id (may differ from page slug)
  const [ownerUsername, setOwnerUsername] = useState<string | undefined>(
    bioData?.username
  );

  // detect html mode safely when data might be undefined
  // treat as html if bio is marked html or when either html or css exists
  // this covers css only pages so font-face in css still loads on published page
  const hasHtml = Boolean(
    bioData &&
      typeof bioData.bioHtml === "string" &&
      (bioData.bioHtml?.length ?? 0) > 0
  );
  const hasCss = Boolean(
    bioData &&
      typeof bioData.bioCss === "string" &&
      (bioData.bioCss?.length ?? 0) > 0
  );
  const cssHasFontFace =
    hasCss && /@font-face\s*\{/.test(String(bioData?.bioCss || ""));
  const isHtmlMode = Boolean(
    bioData &&
      (bioData.bioFormat === "html" || hasHtml || hasCss || cssHasFontFace)
  );

  // when html mode is on render into shadow dom and scope css then detect background
  useEffect(() => {
    if (!isHtmlMode || !bioData) return;
    const host = hostRef.current;
    if (!host) return;
    const root = host.shadowRoot || host.attachShadow({ mode: "open" });
    while (root.firstChild) root.removeChild(root.firstChild);

    // add css reset to eliminate unwanted browser default margins and styles
    const resetStyle = document.createElement("style");
    resetStyle.setAttribute("data-css-reset", "true");
    resetStyle.textContent = `
      /* reset browser default margins and padding that interfere with utility classes */
      *, *::before, *::after {
        margin: 0;
        padding: 0;
        border: 0;
        box-sizing: border-box;
      }
      
      /* reset list styles */
      ul, ol {
        list-style: none;
      }
      
      /* reset headings */
      h1, h2, h3, h4, h5, h6 {
        font-size: inherit;
        font-weight: inherit;
      }
      
      /* reset form elements */
      button, input, textarea, select {
        font: inherit;
        color: inherit;
      }
      
      /* reset links */
      a {
        color: inherit;
        text-decoration: inherit;
      }
      
      /* reset tables */
      table {
        border-collapse: collapse;
        border-spacing: 0;
      }
      
      /* reset images */
      img {
        max-width: 100%;
        height: auto;
        display: block;
      }
    `;
    root.appendChild(resetStyle);

    // user css first for overrides
    const style = document.createElement("style");
    style.setAttribute("data-user-css", "true");
    style.textContent = (bioData.bioCss as string) || "";
    root.appendChild(style);

    // register font faces globally from css so they work in shadow dom
    registerFontFacesFromCss(bioData.bioCss);

    const container = document.createElement("div");
    container.innerHTML = (bioData.bioHtml as string) || "";
    root.appendChild(container);

    // @font-face declared inside <style> tags in the html field also needs to
    // be lifted to the document, since shadow-tree font faces are ignored
    registerFontFacesFromCss(
      Array.from(container.querySelectorAll("style"))
        .map((s) => s.textContent)
        .join("\n"),
    );

    // generate scoped utilities for content inside shadow root
    void applyUtilitiesToTargetRoot(container, root).then(() => {
      // after utilities applied attempt to detect background
      const firstElement = container.firstElementChild as HTMLElement | null;
      let bgToken: string | null = null;
      let inline: string | null = null;
      if (firstElement) {
        bgToken = pickBgUtility(firstElement.getAttribute("class"));
        if (!bgToken) {
          const cs = getComputedStyle(firstElement);
          const bg = cs.backgroundColor;
          if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent")
            inline = bg;
        }
      }
      applyPageBackground(bgToken, inline);
    });
  }, [isHtmlMode, bioData?.id, bioData?.bioHtml, bioData?.bioCss]);

  // resolve canonical username via userId (fetch aggregated profile route which accepts user id)
  useEffect(() => {
    let cancelled = false;
    if (!bioData?.userId) {
      setOwnerUsername(bioData?.username);
      return;
    }
    const current = ownerUsername;
    // always attempt fetch for correctness (cheap)
    (async () => {
      try {
        const res = await fetch(
          `${BASE}/api/profile/${encodeURIComponent(bioData.userId!)}`,
          {
            headers: { Accept: "application/json" },
            credentials: "include",
          }
        );
        if (!res.ok) {
          if (!cancelled) setOwnerUsername(bioData.username);
          return;
        }
        const data = await res.json();
        const canonical =
          (data?.username as string) ||
          (data?.displayUsername as string) ||
          bioData.username;
        if (!cancelled && canonical && canonical !== current) {
          setOwnerUsername(canonical);
        }
      } catch {
        if (!cancelled) setOwnerUsername(bioData.username);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bioData?.userId, bioData?.username]);

  // bml render path: we need to run detection after render, so effect relying on container ref
  useEffect(() => {
    if (isHtmlMode) return;
    const container = bmlContainerRef.current;
    if (!container) return;
    const firstElement = container.firstElementChild as HTMLElement | null;
    let bgToken: string | null = null;
    let inline: string | null = null;
    if (firstElement) {
      bgToken = pickBgUtility(firstElement.getAttribute("class"));
      if (!bgToken) {
        const cs = getComputedStyle(firstElement);
        const bg = cs.backgroundColor;
        if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent")
          inline = bg;
      }
    }
    applyPageBackground(bgToken, inline);
  }, [isHtmlMode, bioData?.id]);

  function pickBgUtility(cls: string | null | undefined): string | null {
    if (!cls) return null;
    // prefer last bg-* token (later utilities usually override earlier)
    const tokens = cls.split(/\s+/).filter(Boolean);
    let found: string | null = null;
    for (const t of tokens) {
      if (t.startsWith("bg-")) found = t; // keep updating so last wins
    }
    return found;
  }

  function applyPageBackground(
    bgToken: string | null,
    inlineColor: string | null
  ) {
    const el = pageRootRef.current;
    if (!el) return;
    const base = "min-h-svh";
    // remove any existing dynamic background marker classes we added earlier
    // strategy: if we stored previous class name in dataset
    const prev = el.dataset.userBgClass;
    if (prev && el.classList.contains(prev)) el.classList.remove(prev);
    el.style.removeProperty("--user-inline-bg");
    el.classList.remove("user-inline-bg");

    // priority: bgToken > inlineColor > configColor > fallback
    if (bgToken) {
      el.classList.add(bgToken);
      el.dataset.userBgClass = bgToken;
    } else if (inlineColor) {
      // map inline color to a css var based utility we control (avoid injecting arbitrary inline style on root)
      el.style.setProperty("--user-inline-bg", inlineColor);
      if (!el.classList.contains("user-inline-bg"))
        el.classList.add("user-inline-bg");
    } else {
      el.dataset.userBgClass = "bg-background";
      if (!el.classList.contains("bg-background"))
        el.classList.add("bg-background");
    }
    if (!el.classList.contains("min-h-svh")) el.classList.add(base);
  }

  if (!bioData) {
    return (
      <div>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      </div>
    );
  }

  const profileDescription =
    bioData.bioConfig?.description ||
    `view ${bioData.username}'s bio page on clove.moe`;
  const profileImage = bioData.bioConfig?.image || "https://clove.moe/icon.png";

  // for bio pages, meta tags are server-rendered by vike (src/pages/app)
  // but we still add structured data client-side for better seo
  // render structured data directly without helmet to avoid nesting with root helmet
  const structuredDataScript = (
    <StructuredData
      data={generateProfilePageSchema(
        bioData.username,
        profileDescription,
        profileImage,
        []
      )}
    />
  );

  if (isHtmlMode) {
    return (
      <div ref={pageRootRef} className="min-h-svh bg-background">
        {structuredDataScript}
        <div className="max-w-4xl mx-auto p-6">
          <div className="rounded-lg">
            <div ref={hostRef} />
          </div>
          <PageFooter bio={bioData} ownerUsername={ownerUsername} />
        </div>
      </div>
    );
  }

  const bmlContent = bioData.bioMarkup || bioData.bio || "";
  const parsed = parseBML(bmlContent);

  if (parsed.error || !parsed.ast) {
    return (
      <div className="min-h-svh bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-accent mb-2">
            error parsing bio
          </h1>
          <p className="text-muted-foreground">
            there was an error rendering this bio
          </p>
        </div>
      </div>
    );
  }

  const renderedBio = renderBML(parsed.ast);

  return (
    <div ref={pageRootRef} className="min-h-svh bg-background">
      {structuredDataScript}
      <div className="max-w-4xl mx-auto p-6">
        <div ref={bmlContainerRef} className="rounded-lg">
          {renderedBio}
          <PageFooter bio={bioData} ownerUsername={ownerUsername} />
        </div>
      </div>
    </div>
  );
}

function PageFooter({
  bio,
  ownerUsername,
}: {
  bio: BioData;
  ownerUsername?: string;
}) {
  const { data: session } = useSession();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const views = bio.viewsCount ?? 0;
  const displayUser = ownerUsername;
  const ownerSlug = ownerUsername;

  // click handler decides between direct edit for owners and seed-for-draft for others
  async function handleEditClick() {
    if (bio.isOwner) {
      const targetId = bio.id;
      if (typeof targetId === "string" && targetId.length > 0) {
        window.location.href = `/editor/${encodeURIComponent(targetId)}`;
        return;
      }
    }
    try {
      const payload: any = {};
      const desiredMode = bio.bioFormat === "html" ? "html" : "bml";
      let seedCode: any = null;
      if (bio.bioFormat === "html") {
        payload.bioFormat = "html";
        payload.bioHtml = bio.bioHtml || "";
        if (bio.bioCss) payload.bioCss = bio.bioCss;
        seedCode = { html: bio.bioHtml || "", css: bio.bioCss || "" };
      } else {
        payload.bioFormat = "bml";
        payload.bioMarkup = bio.bioMarkup || bio.bio || "";
        seedCode = { bml: payload.bioMarkup };
      }
      const res = await fetch(`${BASE}/api/bios/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        if (res.status === 401) {
          window.location.href = "/sign-in";
          return;
        }
        console.warn("failed to seed draft");
        return;
      }
      const data = await res.json();
      const newId = data?.id;
      if (newId) {
        // store seed in sessionstorage for robustness across remounts
        const seed = encodeSeed({ mode: desiredMode as any, code: seedCode });
        try {
          sessionStorage.setItem(`editor-seed-${newId}`, seed);
        } catch (_error) {
          // ignore sessionstorage errors
        }
        const url = `/editor/${encodeURIComponent(
          newId
        )}?mode=${encodeURIComponent(desiredMode)}&seed=${encodeURIComponent(
          seed
        )}`;
        window.location.href = url;
      }
    } catch (e) {
      console.warn("seed draft error", e);
    }
  }

  // show edit button if user is owner regardless of allowEdit toggle
  // non-owners only see it when allowEdit is explicitly true
  const allowEditFlag = bio.bioConfig?.allowEdit === true;
  const canEdit = Boolean(bio.isOwner) || allowEditFlag;

  const canReport = Boolean(session && !bio.isOwner);

  return (
    <>
      <div className="mt-10 pt-6 border-t border-border text-sm text-muted-foreground flex flex-col gap-2">
        <div className="flex flex-wrap gap-4 items-center">
          <span className="inline-flex items-center gap-1">
            <span className="opacity-70">views</span>
            <span className="font-medium text-foreground">{views}</span>
          </span>
          {displayUser && (
            <a
              href={`/profile/${encodeURIComponent(ownerSlug!)}`}
              className="underline-offset-2 hover:underline text-foreground"
            >
              @{displayUser}
            </a>
          )}
          {canEdit && (
            <button
              onClick={handleEditClick}
              className="px-3 py-1 rounded-md bg-accent text-accent-foreground hover:opacity-90 transition text-sm cursor-pointer"
              type="button"
            >
              edit
            </button>
          )}
          {canReport && (
            <button
              onClick={() => setReportDialogOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border hover:bg-accent/20 transition text-sm cursor-pointer"
              type="button"
              title="Report this page"
            >
              <Flag className="w-3.5 h-3.5" />
              report
            </button>
          )}
        </div>
      </div>
      {displayUser && (
        <ReportDialog
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
          subject="page"
          reportedUsername={displayUser}
          reportedUrl={window.location.href}
        />
      )}
    </>
  );
}
